import { useEffect, useState } from 'react';
import { Button} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAccordionContext } from '../contexts/AccordionContext';
import { useFiltersContext } from '../contexts/FiltersContext';
import { useLockersContext } from '../contexts/LockersContext';
import { useSpecificLockerActionContext } from '../contexts/SpecificLockerActionContext';
import { useTrackingContext } from '../contexts/TrackingContext';
import { Locker, PreviouslyUsedLocker, SubscriptionType, ScheduleInterval} from '../data/LockerDataTypes';
import { getDayName, LockerService, removeEasyboxFromLockername, saveUsedLockersToLocalStorage } from '../services/lockerService';
import { ROUTE, STORAGE_BASE_URL } from '../Settings';
import FavouriteLockerComponent from './FavouriteLockerComponent';
import { ClockIcon, POSIcon } from './Miscellaneous';

const PlaceSelector = () => {
    const [scheduleIntervals, setScheduleIntervals] = useState([] as ScheduleInterval[]);
    const { t } = useTranslation();
    const { setAccordionExpanded } = useAccordionContext()
    const { setShowFilters } = useFiltersContext()
    const { lockerPluginOptions } = useLockersContext();
    const {
        selectedLocker,
        setLockerListSelected,
        favouriteLockers,
        previouslyUsedLockers,
        setPreviouslyUsedLockers
    } = useSpecificLockerActionContext();
    const {
        setIsSelectLockerClicked,
        
    } = useTrackingContext();

    const { accordionExpanded } = useAccordionContext();
    const [showPopover, setShowPopover] = useState(false);

    const ProceedAfterLockerSelection = (): void => {
        handlePreviouslyUsedLocker();
        const lockerToPush =
        {
            lockerId: selectedLocker.lockerId,
            name: selectedLocker.name,
            address: selectedLocker.address,
            cityId: selectedLocker.cityId,
            city: selectedLocker.city,
            countyId: selectedLocker.countyId,
            county: selectedLocker.county,
            supportedPayment: selectedLocker.supportedPayment
        };
        
        const response = LockerService.getPostMessage(SubscriptionType.SelectLocker, lockerToPush);
        window?.parent?.postMessage(response, '*');
        setIsSelectLockerClicked(true);
    };

    useEffect(() => {
        calculateScheduleIntervalsForLocker();
        setAccordionExpanded(true);
        setShowFilters(false);
    }, [selectedLocker])

    useEffect(() => {
        if (showPopover && !accordionExpanded) {
            setShowPopover(false);
        }
    }, [accordionExpanded])

    useEffect(()=>{
        saveUsedLockersToLocalStorage(lockerPluginOptions.countryCode, previouslyUsedLockers);
    },[previouslyUsedLockers])

    const backToList = (): void => {
        setLockerListSelected({} as Locker)
    };

    const calculateScheduleIntervalsForLocker = (): void => {
        let interval = {} as ScheduleInterval;
        const finalSchedule = [];

        const lockerSchedule = [...selectedLocker.schedule].sort((x,y) => x.day - y.day);

        interval.startDay = lockerSchedule[0].day;
        interval.openingHour = lockerSchedule[0].openingHour;
        interval.closingHour = lockerSchedule[0].closingHour;
        interval.endDay = lockerSchedule[0].day;

        for (let i = 1; i < lockerSchedule.length; i++) {
            if (lockerSchedule[i].openingHour == lockerSchedule[i - 1].openingHour &&
                lockerSchedule[i].closingHour == lockerSchedule[i - 1].closingHour) {
                interval.endDay = lockerSchedule[i].day;
            }
            else {
                finalSchedule.push(interval)
                interval = {} as ScheduleInterval;
                interval.startDay = lockerSchedule[i].day;
                interval.openingHour = lockerSchedule[i].openingHour;
                interval.closingHour = lockerSchedule[i].closingHour;
                interval.endDay = lockerSchedule[i].day;
            }
        }
        finalSchedule.push(interval)
        setScheduleIntervals(finalSchedule);
    }

    const handlePreviouslyUsedLocker = (): void => {
        let selectedLockerIsPreviouslyUsed = previouslyUsedLockers.findIndex(obj => obj && obj.lockerId === selectedLocker.lockerId) != -1;
        let selectedLockerIsFavourite = favouriteLockers.findIndex(obj => obj.lockerId == selectedLocker.lockerId) != -1 ;

        if (selectedLockerIsFavourite || selectedLockerIsPreviouslyUsed) {
            return;
        }

        let tempPrevouslyUsedLockers = previouslyUsedLockers.slice();
        if(tempPrevouslyUsedLockers.length > 2){
            tempPrevouslyUsedLockers.pop();
        }
        tempPrevouslyUsedLockers.push(getLatestUsedLocker());

        setPreviouslyUsedLockers(tempPrevouslyUsedLockers.sort(
            (a,b)=> {return b?.dateClicked?.valueOf() - a?.dateClicked?.valueOf()}
        ))
    }

    const getLatestUsedLocker = (): PreviouslyUsedLocker => {
        return {
            lockerId: selectedLocker.lockerId,
            lat: selectedLocker.lat,
            lng: selectedLocker.lng,
            dateClicked: new Date
        } as PreviouslyUsedLocker;
    }

    const LockerSchedule = (): any => {
        return (<>
            {
                scheduleIntervals.flatMap(el =>
                    el.startDay == el.endDay ?
                        <div key={el.startDay} className='d-flex justify-content-center'>
                            {getDayName(el.startDay)}: {el.openingHour == '00:00' && el.closingHour == '23:59' ? t('non-stop') : `${el.openingHour} - ${el.closingHour}`}
                        </div>
                        :
                        <div key={el.startDay} className='d-flex justify-content-center'>
                            {getDayName(el.startDay)} - {getDayName(el.endDay)}: {el.openingHour == '00:00' && el.closingHour == '23:59' ? t('non-stop') : `${el.openingHour} - ${el.closingHour}`}
                        </div>
                )}
        </>);
    }

    return (
        <div className="border rounded locker-plugin-locker-selector">
            <Button variant="link" size="sm" className="locker-plugin-link-btn locker-plugin-back-to-list-btn" onClick={backToList}>{t("backToLockerList")}</Button>
            <span className='d-flex justify-content-center align-items-center'>
                <img src={`${STORAGE_BASE_URL}${ROUTE}/PinEasybox.png`} />
                <strong className='a-bit-of-margin-left'>{removeEasyboxFromLockername(selectedLocker.name)}</strong>
            </span>
            <span className='d-flex justify-content-center locker-text-align-center'>{selectedLocker.address}</span>
            <span className='d-flex justify-content-center'>{selectedLocker.city}, {selectedLocker.county}</span>
            <Button variant="link" size="sm" onClick={() => window.open(`https://www.google.ro/maps/place/${selectedLocker.lat},${selectedLocker.lng}`, "_blank")}>
                {t("openOnGoogleMaps")}
            </Button>
            <div className='mt-1 d-flex justify-content-center'>
                <ClockIcon />
                <strong className='a-bit-of-margin-left'>{t("lockerSchedule")}</strong>
            </div>
            <LockerSchedule />
            <div className=' mt-1 d-flex justify-content-center align-items-center'>
                <POSIcon />
                <strong className='a-bit-of-margin-left'>{t('posAtLocation')} {selectedLocker.supportedPayment == 1 ? t('yes') : t('no')}</strong>
            </div>

            <div className='locker-plugin-place-selector-btn-container mx-auto'>
            <FavouriteLockerComponent/>
            <Button variant="primary" size="sm" onClick={ProceedAfterLockerSelection} >{t("selectBtn")}</Button>
            </div>
        </div>
    )
};

export default PlaceSelector;
